import { Select, Table, TableColumnsType } from 'antd';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getExamArchives } from '../../../api/archives.api';
import { getTestTypes } from '../../../api/testCenter.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IExamArchiveItem,
  IGetExamArchives,
} from '../../../models/archives.model';
import { IGetTestTypes, ITestType } from '../../../models/testCenter.model';
import { RootState } from '../../../store/rootReducer';
import { getReverseYears } from '../../../lib/util';

const ArchiveExam = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0005 = 연도
  const SY0005 = commonCodes.find(
    (item) => item.commonCode === 'SY0005',
  )?.detailCode;
  // TE0001 = 시험구분
  const TE0001 = commonCodes.find(
    (item) => item.commonCode === 'TE0001',
  )?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetExamArchives>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetTestTypes>();

  const [examDiv, setExamDiv] = useState<string>();
  const [examTypeCode, setExamTypeCode] = useState<number>();
  const [types, setTypes] = useState<ITestType[] | undefined>([]);
  const [year, setYear] = useState<string>();
  const [examName, setExamName] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});

  useEffect(() => {
    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(_param.pageNo);
    }
    if (query.examDiv) {
      _param.examDiv = String(query.examDiv);
      setExamDiv(_param.examDiv);
    }
    if (query.examTypeCode) {
      _param.examTypeCode = Number(query.examTypeCode);
      setExamTypeCode(_param.examTypeCode);
    }
    if (query.year) {
      _param.year = String(query.year);
      setYear(_param.year);
    }
    if (query.examName) {
      _param.examName = String(query.examName);
      setExamName(_param.examName);
    }

    execute(getExamArchives(_param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  }, [location]);

  useEffect(() => {
    execute2(getTestTypes());
  }, []);

  const onSearch = () => {
    const param: any = { page: 1 };

    if (examDiv) param.examDiv = examDiv;
    if (examTypeCode) param.examTypeCode = examTypeCode;
    if (year) param.year = year;
    if (examName) param.examName = examName;

    history.push(`/archive/exam?${QueryString.stringify(param)}`);
  };

  const onSetExamDiv = (value: string) => {
    setExamDiv(value);
    setExamTypeCode(undefined);
    const list = res2?.data?.filter((item) => item.examDiv === value);
    setTypes(list);
  };

  const columns: TableColumnsType<IExamArchiveItem> = [
    {
      title: t('archive.exam.column1'),
      dataIndex: 'examCode',
      key: 'examCode',
      align: 'center',
      sorter: (a, b) => a.examCode - b.examCode,
    },
    {
      title: t('archive.exam.column2'),
      dataIndex: 'examDiv',
      key: 'examDiv',
      align: 'center',
      sorter: (a, b) => a.examDiv?.localeCompare(b.examDiv),
      render: (value) => getCommonCodeLabel('TE0001', value, 'ko'),
    },
    {
      title: t('archive.exam.column3'),
      dataIndex: 'examTypeName',
      key: 'examTypeName',
      align: 'center',
      sorter: (a, b) => a.examTypeName?.localeCompare(b.examTypeName),
    },
    {
      title: t('common.text.year'),
      dataIndex: 'year',
      key: 'year',
      align: 'center',
      sorter: (a, b) => a.year?.localeCompare(b.year),
    },
    {
      title: t('archive.exam.column4'),
      dataIndex: 'examName',
      key: 'examName',
      align: 'center',
      sorter: (a, b) => a.examName?.localeCompare(b.examName),
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-archive-big.svg"
      parentMenu={t('common.menu.resources')}
      currentMenu={t('archive.exam.title1')}
      onlyTitle={true}
    />	  
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.column.category')}</label>
            <div>
              <Select
                placeholder={t('common.column.category')}
                value={examDiv}
                onChange={(value) => onSetExamDiv(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {TE0001?.map((item: any) => {
                  return (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('archive.exam.column3')}</label>
            <div>
              <Select
                placeholder={t('archive.exam.column3')}
                value={examTypeCode}
                onChange={(value) => setExamTypeCode(value)}
                disabled={!examDiv}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {types?.map((item) => (
                  <Select.Option
                    key={item.examTypeCode}
                    value={item.examTypeCode}
                  >
                    {item.examTypeName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('common.text.year')}</label>
            <div>
              <Select
                placeholder={t('common.text.year')}
                value={year}
                onChange={(value) => setYear(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {getReverseYears(SY0005).map((item: any) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <input
                type="text"
                className="form-control w-240p"
                placeholder={t('archive.exam.placeholder2')}
                value={examName}
                onChange={(e) => setExamName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
              <div className='comm-btn__area'>
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading || isLoading2 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}</div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/archive/exam/${record.examCode}?${QueryString.stringify(
                      param,
                    )}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="archiveCode"
          />
        </div>
        <div className="comm-bx__between">
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/archive/exam"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ArchiveExam;
